import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGetPropertiesMutation } from "../../../store/services/publicApi";
import {
  useGetApplicationsMutation,
  useGetLeaseBalanceQuery,
  useSaveApplicationDataMutation,
  useGetApplicationFeeMutation,
  useFinalizeApplicationMutation,
} from "../../../store/services/privateApi";
import { ManualLoader, Messages } from "../../../components";
import { WizardShell } from "../../../components/Layouts/WizardShell";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { IncomeVerificationWidget } from "./IncomeVerificationWidget";
import { commonSpacing, theme } from "../../../shared/theme/theme";

import {
  RegisterReviewContainer,
  RegisterReviewSection,
} from "./Register.style";
import { PayCreditOrDebitCard } from "./PayCreditOrDebitCard";
import { PayACH } from "./PayACH";
import Footer from "./Footer";
import { parseMoveInTooSoonError } from "../../../helpers/moveInTooSoon";
import type { MoveInTooSoonInfo } from "../../../helpers/moveInTooSoon";
import { MoveInDateCorrection } from "./MoveInDateCorrection";
import {
  buildAvailabilityReusePath,
  toIsoDate,
} from "../../Availability/availabilityReuse";
import { formatMonthlyRent } from "../../../helpers/rentConfirm";
import {
  getApplicationAction,
  isApplicationFeeSettled,
} from "../../../helpers/helperApplications";
import { clearAuthReturnTo } from "../../../shared/utilities/authRedirect";

export const LeasePayment = (props: any) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  let leaseId = searchParams.get("leaseid") ?? searchParams.get("leaseId");
  let applicationId = searchParams.get("applicationId");

  // Pay-first (ResMan parity): arrived with an applicationId but no lease yet.
  // We charge the application fee first; the lease is created on finalize.
  const isPayFirst = !leaseId && !!applicationId;

  const { data, isLoading: isLoadingLeaseBalance }: any =
    useGetLeaseBalanceQuery(leaseId!, { skip: !leaseId });

  // Pay-first amount quote (admin/app fee options) + the lease id we get back
  // from finalize after a successful payment (drives proofup, which needs it).
  const [getApplicationFee] = useGetApplicationFeeMutation();
  const [finalizeApplication, { isLoading: isFinalizing }] =
    useFinalizeApplicationMutation();
  const [feeQuote, setFeeQuote] = useState<any>(null);
  const [payFirstPropertyId, setPayFirstPropertyId] = useState<string>("");
  // Pay-first has no lease-balance query (which is where the legacy flow reads the
  // applicant name), so capture the name from the application instead.
  const [payFirstApplicantName, setPayFirstApplicantName] =
    useState<string>("");
  const [payFirstMoveInDate, setPayFirstMoveInDate] = useState<string>("");
  const [payFirstUnit, setPayFirstUnit] = useState<string>("");
  const [payFirstRent, setPayFirstRent] = useState<number | null>(null);
  const [finalizedLeaseId, setFinalizedLeaseId] = useState<string>("");
  const effectiveLeaseId = leaseId ?? finalizedLeaseId;
  // Pay-first finalize outcomes that are NOT a clean success: 202 (lease being
  // created durably in the background) and 409 (terminal failure, charge reversed).
  const [payFirstProcessing, setPayFirstProcessing] = useState(false);
  const [payFirstError, setPayFirstError] = useState<string>("");
  // 402: finalize reached with NO payment on record. Recoverable — surface the
  // message but keep the payment step available so the applicant can actually pay.
  // Distinct from payFirstError (409), which is terminal and hides the widget.
  const [payFirstPaymentRequired, setPayFirstPaymentRequired] =
    useState<string>("");
  // 409 from the pay-first fee quote's early availability check: the unit was
  // leased out from under the applicant (e.g. a lease created directly in ResMan),
  // so it's no longer Vacant/Notice-to-Vacate. Terminal — hide the payment widget
  // and point the applicant back to the property to pick another unit.
  const [unitUnavailableError, setUnitUnavailableError] = useState<string>("");
  const [moveInTooSoon, setMoveInTooSoon] = useState<MoveInTooSoonInfo | null>(
    null
  );

  // Wait until we know the application still owes a fee before quoting —
  // otherwise a paid/completed reload races /fee → vacancy 409 and shows
  // "unit no longer available" for a unit they already leased.
  const [payFirstFeeAllowed, setPayFirstFeeAllowed] = useState(false);

  const loadPayFirstFee = useCallback(() => {
    if (!isPayFirst || !applicationId || !payFirstFeeAllowed) return;
    getApplicationFee(applicationId).then((res: any) => {
      if (res?.data) {
        setFeeQuote(res.data);
        setUnitUnavailableError("");
        setMoveInTooSoon(null);
        return;
      }
      const tooSoon = parseMoveInTooSoonError(res?.error);
      if (tooSoon) {
        setMoveInTooSoon(tooSoon);
        setUnitUnavailableError("");
        return;
      }
      // Already paid / closed — leave this page; do not treat as unit-gone.
      const errCode = res?.error?.data?.code ?? res?.error?.data?.status;
      if (
        res?.error?.status === 409 &&
        (errCode === "ALREADY_PAID" || errCode === "already_paid")
      ) {
        navigate("/", { replace: true });
        return;
      }
      // 409: the unit is no longer available. Surface it instead of leaving the
      // applicant stuck on a payment screen with no fee quote and no message.
      if (res?.error?.status === 409) {
        setUnitUnavailableError(
          res?.error?.data?.message ??
            res?.error?.data?.error ??
            t("account.leasePayment.unitUnavailableBody")
        );
      }
    });
  }, [
    isPayFirst,
    applicationId,
    payFirstFeeAllowed,
    getApplicationFee,
    navigate,
    t,
  ]);

  useEffect(() => {
    loadPayFirstFee();
  }, [loadPayFirstFee]);

  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [selectedAmountOption, setSelectedAmountOption] = useState<
    "balance" | "totalLeaseBalance"
  >("balance");

  // Pay-first maps the two preset options to the quote: HOH share (admin + 1 app
  // fee) and whole household (admin + app fee × adults). Legacy uses the ledger.
  const balanceAmount = isPayFirst
    ? Number(feeQuote?.hohShare ?? 0)
    : Number(data?.balance ?? 0);
  const totalLeaseBalanceAmount = isPayFirst
    ? Number(feeQuote?.householdTotal ?? 0)
    : Number(data?.totalLeaseBalance ?? 0);
  const selectedPaymentAmount =
    selectedAmountOption === "balance"
      ? balanceAmount
      : totalLeaseBalanceAmount;
  // Quote (pay-first) or lease balance (legacy) has not arrived yet. Both
  // amounts fall back to 0, which reads as a real $0 option. Say Loading
  // until the fetch resolves. A loaded quote of $0 still displays $0.00.
  const amountsLoading = isPayFirst
    ? !feeQuote
    : isLoadingLeaseBalance || !data;
  const formatCurrency = (amount: number) => `$${amount.toFixed(2)}`;
  const formatDisplayedAmount = (amount: number) =>
    amountsLoading
      ? t("account.leasePayment.amountLoading")
      : formatCurrency(amount);

  // Prevent back navigation
  useEffect(() => {
    // Push multiple dummy states to create a strong barrier
    window.history.pushState(null, "", window.location.href);
    window.history.pushState(null, "", window.location.href);
    window.history.pushState(null, "", window.location.href);

    const handlePopState = () => {
      // Push forward multiple times to prevent going back
      window.history.pushState(null, "", window.location.href);
      window.history.pushState(null, "", window.location.href);
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const [getPropertiesForIDMutation, { isLoading }] =
    useGetPropertiesMutation();
  const [propertyDetails, setPropertyDetails] = useState({
    name: "",
    city: "",
    abbreviation: "",
    isProofupVerificationEnabled: false,
    affordableHousing: false,
  });
  useEffect(() => {
    // Legacy uses the lease balance's propertyId; pay-first has no lease yet, so
    // use the application's property (captured from the application fetch).
    const propId = data?.propertyId || payFirstPropertyId;
    if (propId) {
      getPropertiesForIDMutation(propId).then((res) => {
        const { data } = res as { data: any };
        setPropertyDetails(data);
      });
    }
  }, [data, payFirstPropertyId, getPropertiesForIDMutation]);

  // Application details needed to decide whether to surface Proofup income
  // verification after payment.
  const [getApplications] = useGetApplicationsMutation();
  const [appInfo, setAppInfo] = useState<{
    personId?: string;
    role?: string;
    hasGuarantor?: boolean;
    noIncome?: boolean;
    isAffordable?: boolean;
  }>({});
  const [proofupValue, setProofupValue] = useState<string>("");
  const [proofupSkipped, setProofupSkipped] = useState(false);
  // Full applicationData of the current application, kept so we can merge a
  // proofupStatus flag without clobbering the rest of the blob.
  const [appData, setAppData] = useState<any>(null);

  useEffect(() => {
    if (applicationId) {
      getApplications({ id: applicationId })
        .then((res: any) => {
          const apps = res?.data;
          // applicationId comes from the URL (string); app.id may be numeric.
          // Coerce both sides so the match doesn't silently fall back to apps[0]
          // (which would populate gating/personId from the wrong application).
          const app = Array.isArray(apps)
            ? apps.find((a: any) => String(a.id) === String(applicationId)) ??
              apps[0]
            : apps;
          if (app) {
            // Paid / completed / closed applications do not belong on this page.
            // Prefer the shared next-step (e.g. Proofup); otherwise go home.
            if (isApplicationFeeSettled(app)) {
              // Stale soft-recover returnTo must not send them back here on next login.
              clearAuthReturnTo();
              const next = getApplicationAction(app);
              navigate(next?.buildPath() ?? "/", { replace: true });
              return;
            }
            if (isPayFirst) setPayFirstFeeAllowed(true);
            setAppInfo({
              personId: app.person?.id,
              role: app.role ?? "Head of Household",
              hasGuarantor: app.applicationData?.guarantors?.length > 0,
              noIncome: app.applicationData?.noIncome === true,
              isAffordable: app.property?.affordableHousing === true,
            });
            setAppData(app.applicationData ?? {});
            // Pay-first proofup needs a propertyId but there's no lease balance
            // to read it from yet — take it from the application's property.
            if (app.property?.id) setPayFirstPropertyId(app.property.id);
            // Applicant name for the payment page (legacy reads it from the lease
            // balance, which pay-first skips). Prefer the entered name, fall back
            // to the person record.
            const applicantName =
              app.applicationData?.applicantInfo?.name ||
              [app.person?.FirstName, app.person?.LastName]
                .filter(Boolean)
                .join(" ");
            if (applicantName) setPayFirstApplicantName(applicantName);
            const moveIn = toIsoDate(app.moveInDate);
            if (moveIn) setPayFirstMoveInDate(moveIn);
            if (app.unit) setPayFirstUnit(String(app.unit));
            const rent = Number(app.rent);
            setPayFirstRent(Number.isFinite(rent) ? rent : null);
          }
        })
        .catch(() => {
          // Leave appInfo unset — shouldShowProofup is gated on personId, so a
          // failed fetch simply means we don't surface the optional Proofup step.
          // Still allow the fee quote so a transient list failure doesn't brick
          // an unpaid payment page.
          if (isPayFirst) setPayFirstFeeAllowed(true);
        });
    }
  }, [applicationId, getApplications, isPayFirst, navigate]);

  // Mirror the gating used in the application flow: only prompt Proofup when the
  // property has it enabled, the applicant isn't relying on a guarantor / has
  // income (or is themselves a guarantor), and isn't a Live-In Aide. Gating on
  // personId keeps it fail-closed: if the application fetch hasn't resolved or
  // failed, we don't render the widget with an undefined personId.
  // Don't re-prompt if income was already verified — either via the old
  // in-wizard step (legacy `incomeVerification` marker, for applicants who were
  // mid-flow at deploy) or the new post-payment flow (`proofupStatus`). Also
  // makes the post-payment ProofUp idempotent on a payment-URL revisit.
  const proofupAlreadyDone =
    appData?.incomeVerification === "completed" ||
    appData?.proofupStatus === "completed";
  const shouldShowProofup =
    propertyDetails.isProofupVerificationEnabled &&
    Boolean(appInfo.personId) &&
    // Never surface ProofUp on the affordable (LIHTC) flow. Check both the
    // application payload (appInfo) and the dedicated property fetch
    // (propertyDetails) since the application's nested property object doesn't
    // always carry affordableHousing.
    appInfo.isAffordable !== true &&
    propertyDetails.affordableHousing !== true &&
    !proofupAlreadyDone &&
    ((!appInfo.hasGuarantor && !appInfo.noIncome) ||
      appInfo.role === "Guarantor") &&
    appInfo.role !== "Live-In Aide";
  const [value, setValue] = React.useState(0);
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };
  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      "aria-controls": `simple-tabpanel-${index}`,
    };
  }

  const [saveApplicationData] = useSaveApplicationDataMutation();

  const setApplicationStatus = async (status: string) => {
    // await
    saveApplicationData({
      id: applicationId,
      body: { status: status },
    });
  };

  // Pay-first: which preset the applicant chose, mapped to the server's option
  // vocabulary.
  const payFirstOption: "share" | "household" =
    selectedAmountOption === "balance" ? "share" : "household";

  // Pay-first success: the fee is paid, now create the lease (finalize is
  // idempotent and also runs durably server-side). On success we get the
  // leaseId, which proofup needs. Only mark the application completed once a
  // lease exists.
  const handlePayFirstSuccess = async () => {
    clearAuthReturnTo();
    // Do NOT assume success: the fee was charged, but the lease is created by
    // finalize, which returns 200 (lease created), 202 (processing), or 409
    // (terminal failure — the charge has been auto-reversed). RTK Query does not
    // reject on HTTP error statuses, so inspect the resolved result instead of
    // relying on try/catch (a 409 would never throw).
    const res: any = await finalizeApplication(applicationId!);
    const newLeaseId = res?.data?.leaseId;
    if (newLeaseId) {
      setFinalizedLeaseId(newLeaseId);
      setApplicationStatus("completed");
      setPaymentSuccess(true);
      return;
    }
    if (res?.error?.status === 409) {
      // Terminal failure: charge reversed, application cannot complete. Do NOT
      // show the success screen.
      setPayFirstError(
        res?.error?.data?.error ??
          "We couldn't complete your application and your payment has been reversed. Please contact support."
      );
      return;
    }
    if (res?.error?.status === 402) {
      // No payment on record: the charge never went through. Do NOT show the
      // "we've received your payment / finalizing" screen — send the applicant
      // back to complete payment.
      setPayFirstProcessing(false);
      setPaymentSuccess(false);
      setPayFirstPaymentRequired(
        res?.error?.data?.error ??
          "We couldn't confirm your payment. Please complete payment to submit your application."
      );
      return;
    }
    // 202 / transient: payment captured, lease being created durably in the
    // background. Show a processing state — not the success/proofup screen, which
    // needs a leaseId we don't have yet. Resumable from the home screen.
    setPayFirstProcessing(true);
  };

  // $0 due (application fee waived by the site team, or nothing owed): there is no
  // charge to make and the gateway rejects a $0 amount, so we skip the payment widget
  // (and its fee-structure fetch) entirely and complete directly. Pay-first finalizes
  // — the server waives the $0 quote and creates the lease; the balance flow just marks
  // the application completed.
  const handleCompleteNoCharge = async () => {
    if (isPayFirst) {
      // Only the SERVER-FROZEN quote may authorize a no-charge completion. If the
      // quote hasn't loaded (or its fetch failed), feeQuote is null and the amounts
      // above default to 0 — we must NOT read that as "$0 due" and finalize without a
      // charge, which is how applications with a real fee got stranded as unpaid while
      // showing "we've received your payment". Require an explicit $0 quote; otherwise
      // send the applicant back to pay.
      const zeroFeeDue =
        !!feeQuote &&
        Number(feeQuote?.hohShare ?? NaN) === 0 &&
        Number(feeQuote?.householdTotal ?? NaN) === 0;
      if (!zeroFeeDue) {
        setPayFirstPaymentRequired(
          "We couldn't confirm your application fee. Please reload the page and complete payment to submit your application."
        );
        return;
      }
      try {
        await handlePayFirstSuccess();
      } catch {
        setPayFirstProcessing(true);
      }
    } else {
      setPaymentSuccess(true);
      setApplicationStatus("completed");
    }
  };

  // Persist where the applicant is in the (now post-payment) Proofup step so it
  // survives a refresh and stays resumable from the home screen even though the
  // application status is already "completed". Merges into the existing
  // applicationData (guarded on appData so we never overwrite it with a stub).
  const persistProofupStatus = (proofupStatus: "needed" | "completed") => {
    if (!applicationId || !appData) return;
    if (appData.proofupStatus === proofupStatus) return;
    // Never downgrade a completed verification back to "needed" (the "needed"
    // effect re-runs whenever appData changes, including right after completion).
    if (proofupStatus === "needed" && appData.proofupStatus === "completed") {
      return;
    }
    const nextAppData = { ...appData, proofupStatus };
    setAppData(nextAppData);
    saveApplicationData({
      id: applicationId,
      body: { applicationData: nextAppData },
    });
  };

  // Once payment succeeds for a Proofup-eligible applicant, record that income
  // verification is still needed (drives the resume CTA in ApplicationList).
  useEffect(() => {
    if (paymentSuccess && shouldShowProofup && appData) {
      persistProofupStatus("needed");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentSuccess, shouldShowProofup, appData]);

  // When the applicant finishes Proofup, mark it complete so the CTA clears.
  useEffect(() => {
    if (proofupValue === "completed" && appData) {
      persistProofupStatus("completed");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proofupValue, appData]);

  return (
    <>
      {(isLoading || isLoadingLeaseBalance || isFinalizing) && <ManualLoader />}
      <WizardShell>
        <Box
          component={"div"}
          className={"container_content"}
          // ref={elementRef}
          sx={{
            [`@media(min-width: ${theme.breakpoints.values.sm}px)`]: {
              minHeight: 400 + commonSpacing.desktopPaddingB * 4,
            },
          }}
        >
          <RegisterReviewContainer>
            <RegisterReviewSection>
              <Typography fontWeight={"bold"}>
                {t("account.leasePayment.applicantInformation")}
              </Typography>
              <Typography>
                <div style={{ display: "flex" }}>
                  <Box sx={{ width: 0.5 }}>
                    <p>{t("account.leasePayment.applicantName")} :</p>
                  </Box>
                  <div>
                    <p>
                      {" "}
                      {isPayFirst
                        ? payFirstApplicantName
                        : `${data?.firstName ?? ""} ${
                            data?.lastName ?? ""
                          }`}{" "}
                    </p>
                  </div>
                </div>
                {isPayFirst && payFirstUnit !== "" && (
                  <div style={{ display: "flex" }}>
                    <Box sx={{ width: 0.5 }}>
                      <p>{t("account.leasePayment.unit")} :</p>
                    </Box>
                    <div>
                      <p>{payFirstUnit}</p>
                    </div>
                  </div>
                )}
                {isPayFirst && payFirstRent != null && (
                  <div style={{ display: "flex" }}>
                    <Box sx={{ width: 0.5 }}>
                      <p>{t("account.leasePayment.monthlyRent")} :</p>
                    </Box>
                    <div>
                      <p>{formatMonthlyRent(payFirstRent)}</p>
                    </div>
                  </div>
                )}
              </Typography>
            </RegisterReviewSection>
            {paymentSuccess && (
              <>
                <RegisterReviewSection>
                  <Typography fontWeight={"bold"}>
                    {t("account.leasePayment.paymentSuccess")}
                  </Typography>
                  <p>{t("account.leasePayment.paymentSuccessMessage")}</p>
                </RegisterReviewSection>
                {shouldShowProofup && !proofupSkipped && (
                  <RegisterReviewSection>
                    <Typography fontWeight={"bold"}>
                      {t("account.incomeVerification.title")}
                    </Typography>
                    {proofupValue !== "completed" && (
                      <Typography sx={{ mt: 1 }}>
                        {t("account.incomeVerification.postPaymentIntro")}
                      </Typography>
                    )}
                    <IncomeVerificationWidget
                      value={proofupValue}
                      onChange={(val: any) => setProofupValue(val)}
                      options={{
                        personId: appInfo.personId,
                        leaseId: effectiveLeaseId,
                        propertyId: data?.propertyId || payFirstPropertyId,
                      }}
                    />
                    {proofupValue !== "completed" && (
                      <Box sx={{ mt: 1, textAlign: "center" }}>
                        <Button
                          variant="text"
                          color="primary"
                          onClick={() => setProofupSkipped(true)}
                        >
                          {t("account.incomeVerification.skipForNow")}
                        </Button>
                      </Box>
                    )}
                  </RegisterReviewSection>
                )}
              </>
            )}
            {payFirstProcessing && !paymentSuccess && (
              <RegisterReviewSection>
                <Typography fontWeight={"bold"}>
                  {t("account.leasePayment.paymentSuccess")}
                </Typography>
                <p>
                  We've received your payment and are finalizing your
                  application. This can take a moment — you can close this page
                  and resume from your home screen.
                </p>
              </RegisterReviewSection>
            )}
            {payFirstError && !paymentSuccess && (
              <RegisterReviewSection>
                <Messages
                  messageHeading={t("account.leasePayment.cardPaymentError")}
                  messageContent={payFirstError}
                  bottomMargin={4}
                  topMargin={4}
                  hideCloseIcon
                />
              </RegisterReviewSection>
            )}
            {payFirstPaymentRequired &&
              !paymentSuccess &&
              !payFirstProcessing && (
                <RegisterReviewSection>
                  <Messages
                    messageHeading={t("account.leasePayment.cardPaymentError")}
                    messageContent={payFirstPaymentRequired}
                    bottomMargin={4}
                    topMargin={4}
                    hideCloseIcon
                  />
                </RegisterReviewSection>
              )}
            {moveInTooSoon && !paymentSuccess && applicationId && (
              <RegisterReviewSection>
                <MoveInDateCorrection
                  applicationId={applicationId}
                  earliestMoveInDate={moveInTooSoon.earliestMoveInDate}
                  message={moveInTooSoon.message}
                  onSaved={async (isoDate, details) => {
                    setMoveInTooSoon(null);
                    if (isoDate)
                      setPayFirstMoveInDate(toIsoDate(isoDate) ?? isoDate);
                    if (details?.rent != null)
                      setPayFirstRent(Number(details.rent));
                    loadPayFirstFee();
                  }}
                />
              </RegisterReviewSection>
            )}
            {unitUnavailableError && !paymentSuccess && !moveInTooSoon && (
              <RegisterReviewSection>
                <Messages
                  messageHeading={t(
                    "account.leasePayment.unitUnavailableHeading"
                  )}
                  messageContent={unitUnavailableError}
                  bottomMargin={4}
                  topMargin={4}
                  hideCloseIcon
                />
                {propertyDetails.abbreviation && applicationId && (
                  <Button
                    variant="contained"
                    onClick={() => {
                      if (!applicationId) return;
                      navigate(
                        buildAvailabilityReusePath({
                          abbreviation: propertyDetails.abbreviation,
                          applicationId,
                          moveInDate: payFirstMoveInDate || undefined,
                        })
                      );
                    }}
                  >
                    {t("account.leasePayment.viewAvailableUnits")}
                  </Button>
                )}
              </RegisterReviewSection>
            )}
            {!paymentSuccess &&
              !payFirstProcessing &&
              !payFirstError &&
              !unitUnavailableError &&
              !moveInTooSoon && (
                <>
                  <RegisterReviewSection>
                    <Typography fontWeight={"bold"}>
                      {t("account.leasePayment.payment")}
                    </Typography>
                    <Box sx={{ mt: 2, mb: 2 }}>
                      <Typography fontWeight={"bold"}>
                        {t("account.leasePayment.selectAmount")}
                      </Typography>
                      <FormControl fullWidth>
                        <RadioGroup
                          value={selectedAmountOption}
                          onChange={(event) => {
                            setSelectedAmountOption(
                              event.target.value as
                                | "balance"
                                | "totalLeaseBalance"
                            );
                          }}
                        >
                          <FormControlLabel
                            value="balance"
                            control={<Radio />}
                            label={`${t(
                              isPayFirst
                                ? "account.leasePayment.optionYourFee"
                                : "account.leasePayment.optionBalance"
                            )}: ${formatDisplayedAmount(balanceAmount)}`}
                          />
                          <FormControlLabel
                            value="totalLeaseBalance"
                            control={<Radio />}
                            label={`${t(
                              isPayFirst
                                ? "account.leasePayment.optionHouseholdFees"
                                : "account.leasePayment.optionTotalLeaseBalance"
                            )}: ${formatDisplayedAmount(
                              totalLeaseBalanceAmount
                            )}`}
                          />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                    <Typography sx={{ mb: 2 }}>
                      {`${t(
                        "account.leasePayment.payAmount"
                      )}: ${formatDisplayedAmount(selectedPaymentAmount)}`}
                    </Typography>
                    {isPayFirst &&
                    !feeQuote &&
                    !unitUnavailableError &&
                    !moveInTooSoon ? (
                      <Box
                        sx={{
                          mt: 2,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <CircularProgress size={40} />
                      </Box>
                    ) : selectedPaymentAmount > 0 ? (
                      <>
                        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                          <Tabs
                            value={value}
                            aria-label="basic tabs example"
                            onChange={handleChange}
                          >
                            <Tab
                              label={t("account.leasePayment.cardTab")}
                              {...a11yProps(0)}
                            />
                            <Tab
                              label={t("account.leasePayment.achTab")}
                              {...a11yProps(1)}
                            />
                          </Tabs>
                        </Box>
                        {value === 0 && (
                          <PayCreditOrDebitCard
                            onPaymentSuccess={async () => {
                              if (isPayFirst) {
                                // Await + guard: handlePayFirstSuccess inspects the result
                                // (RTK doesn't reject on HTTP errors), but if finalize ever
                                // rejects, the charge already succeeded — fall back to the
                                // processing state and let the durable retry create the lease.
                                try {
                                  await handlePayFirstSuccess();
                                } catch {
                                  setPayFirstProcessing(true);
                                }
                              } else {
                                setPaymentSuccess(true);
                                setApplicationStatus("completed");
                              }
                            }}
                            amount={selectedPaymentAmount}
                            leaseId={leaseId!}
                            payFirst={
                              isPayFirst
                                ? {
                                    applicationId: applicationId!,
                                    option: payFirstOption,
                                  }
                                : undefined
                            }
                            onMoveInTooSoon={setMoveInTooSoon}
                          />
                        )}
                        {value === 1 && (
                          <PayACH
                            onPaymentSuccess={() => {
                              if (isPayFirst) {
                                // ACH is settlement-gated: the bank payment is only
                                // submitted now; the lease is created server-side after
                                // it settles (payment-processed webhook). Show the
                                // processing state rather than calling finalize (which
                                // would be premature before settlement).
                                setPayFirstProcessing(true);
                              } else {
                                setPaymentSuccess(true);
                                setApplicationStatus("completed");
                              }
                            }}
                            amount={selectedPaymentAmount}
                            leaseId={leaseId!}
                            payFirst={
                              isPayFirst
                                ? {
                                    applicationId: applicationId!,
                                    option: payFirstOption,
                                  }
                                : undefined
                            }
                            onMoveInTooSoon={setMoveInTooSoon}
                          />
                        )}
                      </>
                    ) : (
                      // $0 due — nothing to collect (fee waived / zero balance). Skip the
                      // payment widget and its fee-structure fetch entirely; let the
                      // applicant complete without a charge. The amount radios above stay
                      // visible so they can still opt to pay a non-zero balance.
                      <Box sx={{ mt: 2 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            color: "text.secondary",
                            mb: 2,
                          }}
                        >
                          {t("account.leasePayment.noFeeDueHelp")}
                        </Typography>
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={handleCompleteNoCharge}
                        >
                          {t("account.leasePayment.completeApplication")}
                        </Button>
                      </Box>
                    )}
                  </RegisterReviewSection>
                </>
              )}
          </RegisterReviewContainer>
        </Box>
        <Box>
          <Footer />
        </Box>
      </WizardShell>
    </>
  );
};
